import { z } from 'zod';
import { PlaceTitleSchema, type PlaceTitle } from '../../../renderer/src/place-title';

const LEVELS = ['locality', 'place', 'district', 'region', 'country'] as const;
const NamedArea = z.object({ name: z.string() });
const Feature = z.object({ properties: z.object({
  feature_type: z.string(), name: z.string(),
  context: z.object({ locality: NamedArea.optional(), place: NamedArea.optional(),
    district: NamedArea.optional(), region: NamedArea.optional(), country: NamedArea.optional() }).optional(),
}) });
const Response = z.object({ features: z.array(z.unknown()) });
const label = (name: string | undefined) => name?.trim() && name.trim().length <= 120 ? name.trim() : undefined;

/** Prefers a settlement or official locality, then a broader geopolitical area. */
export function startingPlaceTitle(candidate: unknown): PlaceTitle | undefined {
  const response = Response.safeParse(candidate);
  if (!response.success) return undefined;
  const areas = response.data.features.flatMap(value => {
    const feature = Feature.safeParse(value);
    return feature.success ? [feature.data.properties] : [];
  });
  const nameAt = (level: typeof LEVELS[number]) => {
    for (const area of areas) {
      const name = label(area.feature_type === level ? area.name : area.context?.[level]?.name);
      if (name) return name;
    }
    return undefined;
  };
  for (const level of LEVELS) {
    const name = nameAt(level);
    if (!name) continue;
    const candidates = level === 'country' ? [] : level === 'region'
      ? [nameAt('country')] : [nameAt('region'), nameAt('country')];
    const region = candidates.find(value => value && value.toLocaleLowerCase() !== name.toLocaleLowerCase()) ?? '';
    return PlaceTitleSchema.parse({ name, region });
  }
  return undefined;
}
